import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Product } from "@/lib/api";
import { Clock, Tag, ShoppingCart, X, Info, Ticket, Loader2, CheckCircle } from "lucide-react";
import { CheckoutDialog } from "./CheckoutDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ProductDetailDialogProps {
  product: Product | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export interface AppliedVoucher {
  id: string;
  code: string;
  discountType: string;
  discountValue: number;
  maxDiscount: number | null;
  minPurchase: number | null;
}

const categoryColors: Record<string, string> = {
  netflix: "from-red-500 to-red-700",
  spotify: "from-green-500 to-green-700",
  youtube: "from-red-600 to-red-800",
  chatgpt: "from-teal-500 to-teal-700",
  canva: "from-blue-500 to-purple-600",
  capcut: "from-pink-500 to-purple-500",
  disney: "from-blue-700 to-blue-900",
  gemini: "from-blue-400 to-indigo-500",
  hbo: "from-purple-600 to-purple-800",
  prime: "from-sky-500 to-sky-700",
  viu: "from-amber-400 to-amber-600",
  default: "from-primary to-secondary",
};

const categoryIcons: Record<string, string> = {
  netflix: "N",
  spotify: "🎵",
  youtube: "▶️",
  chatgpt: "🤖",
  canva: "C",
  capcut: "✂️",
  disney: "D+",
  gemini: "✨",
  hbo: "HBO",
  prime: "P",
  viu: "V",
  default: "📱",
};

export function ProductDetailDialog({ product, open, onOpenChange }: ProductDetailDialogProps) {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState<AppliedVoucher | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const { toast } = useToast();
  
  if (!product) return null;
  const bgGradient = categoryColors[product.categorySlug] || categoryColors.default;
  const icon = categoryIcons[product.categorySlug] || categoryIcons.default;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      private: "Private",
      sharing_1p1u: "Sharing 1P1U",
      sharing: "Sharing",
      invite: "Invite",
      family_head: "Family Head",
      family_plan_invite: "Family Plan Invite",
      voucher: "Voucher",
      individual: "Individual",
    };
    return labels[type] || type;
  };

  const calculateDiscount = (originalPrice: number, voucher: AppliedVoucher) => {
    let discount = 0;
    if (voucher.discountType === "percentage") {
      discount = Math.floor(originalPrice * (voucher.discountValue / 100));
      if (voucher.maxDiscount && discount > voucher.maxDiscount) {
        discount = voucher.maxDiscount;
      }
    } else {
      discount = voucher.discountValue;
    }
    return discount;
  };

  const getDiscountedPrice = () => {
    if (!appliedVoucher) return product.price;
    const discount = calculateDiscount(product.price, appliedVoucher);
    return Math.max(0, product.price - discount);
  };

  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) {
      toast({
        title: "Kode voucher kosong",
        description: "Masukkan kode voucher terlebih dahulu",
        variant: "destructive",
      });
      return;
    }

    setIsValidating(true);
    try {
      const { data, error } = await supabase
        .from("vouchers")
        .select("*")
        .eq("code", voucherCode.trim().toUpperCase())
        .eq("is_active", true)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        toast({
          title: "Voucher tidak valid",
          description: "Kode voucher tidak ditemukan atau sudah tidak aktif",
          variant: "destructive",
        });
        return;
      }

      // Check validity period
      const now = new Date();
      if (data.valid_from && new Date(data.valid_from) > now) {
        toast({
          title: "Voucher belum berlaku",
          description: "Voucher ini belum dapat digunakan",
          variant: "destructive",
        });
        return;
      }
      if (data.valid_until && new Date(data.valid_until) < now) {
        toast({
          title: "Voucher sudah kadaluarsa",
          description: "Voucher ini sudah melewati masa berlaku",
          variant: "destructive",
        });
        return;
      }

      // Check usage limit
      if (data.usage_limit && data.used_count >= data.usage_limit) {
        toast({
          title: "Voucher habis",
          description: "Kuota voucher ini sudah habis",
          variant: "destructive",
        });
        return;
      }

      // Check minimum purchase
      if (data.min_purchase && product.price < data.min_purchase) {
        toast({
          title: "Minimum pembelian tidak terpenuhi",
          description: `Minimum pembelian ${formatPrice(data.min_purchase)}`,
          variant: "destructive",
        });
        return;
      }

      setAppliedVoucher({
        id: data.id,
        code: data.code,
        discountType: data.discount_type,
        discountValue: data.discount_value,
        maxDiscount: data.max_discount,
        minPurchase: data.min_purchase,
      });

      toast({
        title: "Voucher berhasil diterapkan!",
        description: `Diskon ${data.discount_type === "percentage" ? `${data.discount_value}%` : formatPrice(data.discount_value)}`,
      });
    } catch (error) {
      console.error("Error validating voucher:", error);
      toast({
        title: "Gagal memvalidasi voucher",
        description: "Terjadi kesalahan, silakan coba lagi",
        variant: "destructive",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveVoucher = () => {
    setAppliedVoucher(null);
    setVoucherCode("");
  };

  const discountedPrice = getDiscountedPrice();
  const hasDiscount = appliedVoucher && discountedPrice < product.price;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 max-h-[85vh] overflow-y-auto">
        {/* Header with gradient */}
        <div className={`h-28 bg-gradient-to-br ${bgGradient} flex items-center justify-center`}>
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-5xl text-white font-bold drop-shadow-lg">
              {product.categorySlug?.toUpperCase() || icon}
            </span>
          )}
        </div>

        <div className="p-6 space-y-4">
          {/* Product title and category */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${bgGradient} flex items-center justify-center`}>
                <span className="text-sm text-white font-bold">{icon}</span>
              </div>
              <h2 className="text-xl font-bold text-foreground">{product.name}</h2>
            </div>
            <Badge variant="outline" className="text-xs">
              {product.categorySlug || "Apps"}
            </Badge>
          </div>

          {/* Product info grid */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Durasi</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                <Clock className="h-4 w-4 text-primary" />
                <span className="font-semibold text-foreground">{product.duration} {product.durationUnit}</span>
              </div>
            </div>
            <div className="text-center border-x border-border">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Tipe</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                <Tag className="h-4 w-4 text-warning" />
                <span className="font-semibold text-foreground">{getTypeLabel(product.type)}</span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Harga</p>
              <div className="flex flex-col items-center justify-center mt-1">
                {hasDiscount ? (
                  <>
                    <span className="text-xs text-muted-foreground line-through">{formatPrice(product.price)}</span>
                    <span className="font-bold text-success">{formatPrice(discountedPrice)}</span>
                  </>
                ) : (
                  <span className="font-bold text-primary">{formatPrice(product.price)}</span>
                )}
              </div>
            </div>
          </div>

          {/* Voucher Input */}
          <div className="p-4 bg-muted/30 rounded-lg border border-dashed border-border">
            <div className="flex items-center gap-2 mb-2">
              <Ticket className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Punya Voucher?</span>
            </div>
            {appliedVoucher ? (
              <div className="flex items-center justify-between p-2 bg-success/10 rounded-lg border border-success/20">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-success" />
                  <span className="text-sm font-medium text-success">{appliedVoucher.code}</span>
                  <Badge variant="secondary" className="text-xs">
                    {appliedVoucher.discountType === "percentage" 
                      ? `-${appliedVoucher.discountValue}%` 
                      : `-${formatPrice(appliedVoucher.discountValue)}`}
                  </Badge>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleRemoveVoucher}
                  className="text-destructive hover:text-destructive h-7 px-2"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  placeholder="Masukkan kode voucher"
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                  className="flex-1 uppercase"
                  onKeyDown={(e) => e.key === "Enter" && handleApplyVoucher()}
                />
                <Button 
                  variant="secondary" 
                  onClick={handleApplyVoucher}
                  disabled={isValidating}
                  className="shrink-0"
                >
                  {isValidating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Pakai"
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <Tabs defaultValue="description" className="w-full">
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="description">Deskripsi</TabsTrigger>
              <TabsTrigger value="terms">S&K</TabsTrigger>
              <TabsTrigger value="reviews">Ulasan</TabsTrigger>
            </TabsList>
            <TabsContent value="description" className="mt-4 min-h-[80px]">
              <p className="text-sm text-muted-foreground">
                {product.description || "Tidak ada deskripsi tersedia."}
              </p>
              {product.note && (
                <div className="flex items-start gap-2 mt-3 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{product.note}</span>
                </div>
              )}
            </TabsContent>
            <TabsContent value="terms" className="mt-4 min-h-[80px]">
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Akun akan dikirim melalui WhatsApp setelah pembayaran.</li>
                <li>• Garansi sesuai durasi yang tertera.</li>
                <li>• Tidak boleh mengubah password atau email.</li>
                <li>• Jika ada masalah, hubungi admin untuk penggantian.</li>
              </ul>
            </TabsContent>
            <TabsContent value="reviews" className="mt-4 min-h-[80px]">
              <p className="text-sm text-muted-foreground text-center py-4">
                Belum ada ulasan untuk produk ini.
              </p>
            </TabsContent>
          </Tabs>

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <Button 
              className="flex-1 gradient-primary text-primary-foreground"
              disabled={product.stock === "outOfStock"}
              onClick={() => setCheckoutOpen(true)}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Beli Sekarang
            </Button>
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4 mr-2" />
              Tutup
            </Button>
          </div>
        </div>
      </DialogContent>
      
      <CheckoutDialog 
        product={product}
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        appliedVoucher={appliedVoucher}
        discountedPrice={discountedPrice}
      />
    </Dialog>
  );
}
